import { toRaw } from "vue";

const normalizeAddress = (value, label) => {
  const address = typeof value === "string" ? value.trim() : "";
  if (!/^0x[0-9a-fA-F]{40}$/.test(address)) {
    throw new Error(`${label} is missing or invalid.`);
  }
  return address;
};

const sameAddress = (left, right) =>
  left.toLowerCase() === right.toLowerCase();

const normalizeChainId = (value) => {
  const chainId =
    typeof value === "string" && /^0x/i.test(value)
      ? Number.parseInt(value, 16)
      : Number(value);
  if (!Number.isSafeInteger(chainId) || chainId < 0) {
    throw new Error("Injected wallet provider returned an invalid chain.");
  }
  return chainId;
};

export const readInjectedWalletChainId = async (provider) => {
  const walletProvider = toRaw(provider);
  const injectedProvider =
    typeof walletProvider?.request === "function"
      ? walletProvider
      : toRaw(walletProvider?.provider);
  if (typeof injectedProvider?.request === "function") {
    return normalizeChainId(
      await injectedProvider.request({ method: "eth_chainId" })
    );
  }
  throw new Error("Selected injected wallet provider is unavailable.");
};

export const resolveInjectedWalletAuthority = async ({
  provider,
  currentAccountAddress,
  trackedAccounts,
} = {}) => {
  if (typeof provider?.request !== "function") {
    throw new Error("Selected injected wallet provider is unavailable.");
  }
  const providerAccounts = await provider.request({ method: "eth_accounts" });
  const liveAddress = normalizeAddress(
    Array.isArray(providerAccounts) ? providerAccounts[0] : null,
    "Injected wallet active account"
  );
  const currentAddress = normalizeAddress(
    currentAccountAddress,
    "Current wallet account"
  );
  const trackedAddress = normalizeAddress(
    Array.isArray(trackedAccounts) ? trackedAccounts[0] : null,
    "Tracked wallet account"
  );
  if (!sameAddress(liveAddress, currentAddress)) {
    throw new Error(
      "Current wallet account does not match the injected wallet active account."
    );
  }
  if (!sameAddress(liveAddress, trackedAddress)) {
    throw new Error(
      "Tracked wallet account does not match the injected wallet active account."
    );
  }
  return { address: liveAddress, provider };
};

export const requireMatchingWalletAddress = (
  actual,
  expected,
  label = "Wallet address"
) => {
  const actualAddress = normalizeAddress(actual, label);
  const expectedAddress = normalizeAddress(expected, "Expected wallet address");
  if (!sameAddress(actualAddress, expectedAddress)) {
    throw new Error(`${label} does not match the active wallet account.`);
  }
  return actualAddress;
};
